import React, { useState } from 'react';
import { Box, Button, Menu, MenuItem, Typography, Tooltip, CircularProgress } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTenant } from '../contexts/TenantContext';
import { isGolfOSVariant } from '../config/branding';

export const TenantSelector: React.FC = () => {
  const { availableTenants, currentTenant, loading, switchTenant } = useTenant();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTenantChange = (tenantCode: string) => {
    switchTenant(tenantCode);
    handleClose();
  };

  const getTenantDisplayName = (tenant: any) => {
    if (!tenant) {
      return 'No tenant';
    }
    return tenant.name || tenant.code;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading tenants...</Typography>
      </Box>
    );
  }

  if (!currentTenant || availableTenants.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessIcon color="error" />
        <Typography variant="body2" color="error">
          No tenant available
        </Typography>
      </Box>
    );
  }

  if (isGolfOSVariant) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.75,
          py: 0.75,
          borderRadius: 2,
          backgroundColor: 'rgba(45, 106, 47, 0.09)',
          border: '1px solid rgba(45, 106, 47, 0.2)',
        }}
      >
        <BusinessIcon sx={{ color: '#2D6A2F', fontSize: 20 }} />
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
            Club workspace
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ color: '#1A3F1B' }}>
            {getTenantDisplayName(currentTenant)}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Always show the selector if there are multiple tenants available
  // But if only one tenant, show static display
  if (availableTenants.length <= 1) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessIcon color="primary" />
        <Typography variant="body2">{getTenantDisplayName(currentTenant)}</Typography>
      </Box>
    );
  }

  // Filter out default and sample tenants from the dropdown
  const filteredTenants = availableTenants.filter(tenant => {
    const tenantCode = tenant.code.toLowerCase();
    const tenantName = (tenant.name || '').toLowerCase();
    
    // Hide tenants with codes or names containing 'default' or 'sample'
    return !tenantCode.includes('default') && 
           !tenantCode.includes('sample') &&
           !tenantName.includes('default') &&
           !tenantName.includes('sample') &&
           tenant.code !== 'default';
  });

  // If after filtering we only have one tenant (or none), show static display
  if (filteredTenants.length <= 1) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessIcon color="primary" />
        <Typography variant="body2">{getTenantDisplayName(currentTenant)}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Tooltip title="Switch tenant">
        <Button
          id="tenant-selector-button"
          aria-controls={open ? 'tenant-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          endIcon={<KeyboardArrowDownIcon />}
          startIcon={<BusinessIcon />}
          sx={{
            textTransform: 'none',
            color: '#1C5195',
          }}
        >
          {getTenantDisplayName(currentTenant)}
        </Button>
      </Tooltip>
      <Menu
        id="tenant-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'tenant-selector-button',
        }}
      >
        {filteredTenants.map(tenant => (
          <MenuItem
            key={tenant.code}
            onClick={() => handleTenantChange(tenant.code)}
            selected={currentTenant?.code === tenant.code}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon fontSize="small" color={tenant.isDefault ? 'secondary' : 'inherit'} />
              <Box>
                <Typography component="span">{getTenantDisplayName(tenant)}</Typography>
                {tenant.isDefault && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ ml: 1, color: 'text.secondary' }}
                  >
                    (Default)
                  </Typography>
                )}
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
